import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Understanding Financial Well-Being',
    content: [
      '<strong>Learning Goal:</strong> Define financial well-being, understand its components, and recognize how money attitudes affect your overall wellness.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• What is financial well-being (beyond just having money)',
      '• The difference between financial health and financial stress',
      '• How money beliefs and emotions influence financial decisions',
      '• Introduction to the InCharge Financial Distress/Financial Well-Being Scale (IFDFW)',
      '<br/>',
      '<strong>Discussion:</strong> Financial well-being isn\'t just about wealth–it\'s about feeling secure, confident, and in control of your financial life. This sense of security allows you to focus on other important aspects of wellness.'
    ]
  },
  {
    title: 'What is Financial Well-Being?',
    content: [
      'Financial well-being goes beyond having money–it\'s a state of being where you can meet your financial obligations, feel secure about your future, and make choices that allow you to enjoy life.',
      '<br/>',
      '<strong>Key Components:</strong>',
      '• <strong>Financial Security:</strong> Having enough resources to handle emergencies and unexpected expenses',
      '• <strong>Financial Freedom:</strong> The ability to make choices without constant money worries',
      '• <strong>Financial Confidence:</strong> Feeling in control of your financial situation',
      '• <strong>Financial Peace:</strong> Experiencing low financial stress and anxiety',
      '<br/>',
      '<strong>The IFDFW Scale:</strong> This assessment measures both financial distress (worry, lack of control) and financial well-being (security, confidence). It helps identify areas where you feel most and least secure financially.'
    ]
  },
  {
    title: 'Financial Health vs. Financial Stress',
    content: [
      'Understanding the difference between objective financial status and subjective financial well-being is crucial for holistic wellness.',
      '<br/>',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header pillar-cell">ASPECT</div>
          <div class="table-header means-cell">FINANCIAL HEALTH (Objective)</div>
          <div class="table-header matters-cell">FINANCIAL WELL-BEING (Subjective)</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Focus</div>
          <div class="table-cell means-cell">Income, savings, debt levels</div>
          <div class="table-cell matters-cell">Feelings of security and control</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Example</div>
          <div class="table-cell means-cell">$50K savings account</div>
          <div class="table-cell matters-cell">Peace of mind about emergencies</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Measurement</div>
          <div class="table-cell means-cell">Bank statements, credit reports</div>
          <div class="table-cell matters-cell">Personal feelings and confidence</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Key Insight:</strong> Two people with identical financial situations can have very different levels of financial well-being based on their perceptions, beliefs, and emotional relationship with money.'
    ]
  },
  {
    title: 'The Psychology of Money',
    content: [
      'Your beliefs, emotions, and habits around money significantly influence your financial well-being and overall life satisfaction.',
      '<br/>',
      '<strong>Common Money Beliefs:</strong>',
      '• "Money is the root of all evil" (limits abundance mindset)',
      '• "I\'ll never be good with money" (creates self-fulfilling prophecy)',
      '• "More money equals more happiness" (ignores emotional fulfillment)',
      '• "I don\'t deserve financial success" (blocks opportunities)',
      '<br/>',
      '<strong>Emotional Responses to Money:</strong>',
      '• <strong>Anxiety:</strong> Constant worry about bills and expenses',
      '• <strong>Guilt:</strong> Feeling bad about spending or enjoying money',
      '• <strong>Shame:</strong> Embarrassment about financial situation',
      '• <strong>Freedom:</strong> Relief from financial pressure',
      '• <strong>Confidence:</strong> Security in financial decision-making',
      '<br/>',
      '<strong>Why This Matters:</strong> Understanding your money psychology helps you make decisions that align with your values and reduce unnecessary financial stress.'
    ],
    additionalSection: {
      title: 'Money Mindset Reflection Activity',
      content: [
        'Take a moment to reflect on your relationship with money:',
        '<br/>',
        '<strong>Complete this prompt:</strong> "When I think about money, I feel ___ because ___."',
        '<br/>',
        '<strong>Consider these questions:</strong>',
        '• What messages about money did you receive growing up?',
        '• How do your cultural or family beliefs influence your spending?',
        '• What emotions come up when you think about your financial future?',
        '<br/>',
        '<strong>Reflection:</strong> Understanding your money mindset is the first step toward building financial well-being. Your beliefs about money shape your financial reality.'
      ]
    }
  },
  {
    title: 'Introduction to IFDFW Scale',
    content: [
      'The InCharge Financial Distress/Financial Well-Being Scale (IFDFW) is a scientifically validated tool that measures eight key dimensions of financial well-being.',
      '<br/>',
      '<strong>The 8 IFDFW Dimensions:</strong>',
      '1. <strong>Worry about expenses</strong> - Current financial anxiety',
      '2. <strong>Confidence in handling emergencies</strong> - Emergency preparedness',
      '3. <strong>Paycheck-to-paycheck living</strong> - Income stability',
      '4. <strong>Satisfaction with current finances</strong> - Present financial contentment',
      '5. <strong>Sense of control</strong> - Financial decision-making confidence',
      '6. <strong>Worry about future finances</strong> - Future financial security',
      '7. <strong>Overall financial stress</strong> - General financial anxiety',
      '8. <strong>Overall financial well-being</strong> - General financial satisfaction',
      '<br/>',
      '<strong>How It Works:</strong> You rate each statement on a scale from 1 (Strongly Disagree) to 5 (Strongly Agree). Higher scores indicate greater financial well-being and lower distress.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 1: Understanding Financial Well-Being! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about financial well-being concepts.',
        'The quiz will help you review key concepts about money psychology and financial security.'
      ]
    }
  }
];

const FinancialModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('financialM1');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/financial-module-1/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/financial-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left financial-panel"
        >
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Understanding Financial Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Define financial well-being, understand its components, and recognize how money attitudes affect your overall wellness.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                                onClick={() => navigate('/panel/learn/financial-module-1/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule1Page;

